// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {useShouldAnimate} from '@app/features/app/hooks/useShouldAnimate';
import styles from '@app/features/cosmetics/components/CosmeticsSettingsTab.module.css';
import {NameplateBackground} from '@app/features/cosmetics/components/NameplateBackground';
import {
	RoomOutfitEditorForm,
	type RoomOutfitEditorFormState,
} from '@app/features/cosmetics/components/RoomOutfitEditorForm';
import Cosmetics, {
	COSMETIC_TYPES,
	type CosmeticItem,
	type CosmeticSelection,
	type CosmeticType,
	ROOM_CHARACTER_TYPE,
} from '@app/features/cosmetics/state/Cosmetics';
import {getCosmeticAssetURL} from '@app/features/cosmetics/utils/CosmeticAssetUtils';
import {
	filterInventory,
	type InventoryFilter,
	type InventoryQuery,
	inventorySets,
	isCosmeticLocked,
	pickCosmetic,
} from '@app/features/cosmetics/utils/CosmeticInventory';
import {COSMETIC_TYPE_DESCRIPTIONS, COSMETIC_TYPE_LABELS} from '@app/features/cosmetics/utils/CosmeticLabels';
import {cosmeticName} from '@app/features/cosmetics/utils/CosmeticText';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import {
	SOMETHING_WENT_WRONG_DESCRIPTOR,
	TRY_AGAIN_DESCRIPTOR,
	TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {shopSetName} from '@app/features/shop/utils/ShopSets';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as UnsavedChangesCommands from '@app/features/ui/commands/UnsavedChangesCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import {Spinner} from '@app/features/ui/components/Spinner';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {EditBadgesButton} from '@app/features/user/components/modals/tabs/my_profile_tab/EditBadgesButton';
import {ProfilePreview} from '@app/features/user/components/profile/ProfilePreview';
import Users from '@app/features/user/state/Users';
import type {CosmeticAssetDataResponse} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	CheckIcon,
	CrownIcon,
	LockSimpleIcon,
	MagnifyingGlassIcon,
	ProhibitIcon,
	StorefrontIcon,
	WarningIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

// Matches the settings tab type, which is what the unsaved changes bar is keyed by.
const TAB_ID = 'profile_cosmetics';
const TILE_IMAGE_CSS_SIZE = 72;
const SLOT_IMAGE_CSS_SIZE = 48;
// Long lists are drawn a page at a time; the admin-curated catalog rarely fills more than one.
const PAGE_SIZE = 36;
const ALL_QUERY: InventoryQuery = {type: 'all', filter: 'all', search: '', set: null};

const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Profile decorations settings: the tile that takes a cosmetic off its slot.',
});
const LOCKED_DESCRIPTOR = msg({
	message: '{name} comes with {premiumProductName}.',
	comment:
		'Profile decorations settings: shown when a Plus-only decoration is picked without Plus. {name} is the decoration name.',
});
const WORN_TITLE_DESCRIPTOR = msg({
	message: 'Worn now',
	comment: 'Profile decorations settings: title of the row that shows the decoration worn in each slot.',
});
const LOCKED_TILE_DESCRIPTOR = msg({
	message: '{name}, locked: comes with {premiumProductName}',
	comment:
		'Profile decorations settings: screen reader label of a Plus-only decoration tile for a user without Plus. {name} is the decoration name.',
});
const TAKE_OFF_SLOT_DESCRIPTOR = msg({
	message: 'Take off: {slot}',
	comment:
		'Profile decorations settings: screen reader label of the button that takes a slot off. {slot} is the slot name, such as Avatar decoration.',
});
const EMPTY_SLOT_DESCRIPTOR = msg({
	message: 'Empty',
	comment: 'Profile decorations settings: a slot with no decoration worn.',
});
const TAKE_OFF_DESCRIPTOR = msg({
	message: 'Take off',
	comment: 'Profile decorations settings: button that takes the worn decoration off its slot.',
});
const WORN_BADGE_DESCRIPTOR = msg({
	message: 'Worn',
	comment: 'Profile decorations settings: badge on the tile of the decoration being worn. Keep it very short.',
});
const ALL_TYPES_DESCRIPTOR = msg({
	message: 'All types',
	comment: 'Profile decorations settings: button that shows every decoration type again after one slot was opened.',
});
const FILTER_ALL_DESCRIPTOR = msg({
	message: 'Everything',
	comment: 'Profile decorations settings: filter that shows every decoration, owned, free and Plus.',
});
const FILTER_OWNED_DESCRIPTOR = msg({
	message: 'Owned',
	context: 'cosmetic-inventory',
	comment: 'Profile decorations settings: filter that shows only decorations the user bought or was given.',
});
const FILTER_LOCKED_DESCRIPTOR = msg({
	message: 'With {premiumProductName}',
	comment:
		'Profile decorations settings: filter that shows the decorations Plus unlocks. Preserve {premiumProductName}.',
});
const SEARCH_DESCRIPTOR = msg({
	message: 'Search decorations',
	comment: 'Profile decorations settings: placeholder of the search field over the inventory.',
});
const ALL_SETS_DESCRIPTOR = msg({
	message: 'All sets',
	comment: 'Profile decorations settings: chip that clears the set filter.',
});
const SHOW_MORE_DESCRIPTOR = msg({
	message: 'Show {count} more',
	comment: 'Profile decorations settings: button that draws the next page of the inventory. {count} is a number.',
});
const NO_RESULTS_DESCRIPTOR = msg({
	message: 'Nothing matches this search.',
	comment: 'Profile decorations settings: empty state when the search or filters match no decoration.',
});
const EMPTY_OWNED_TITLE_DESCRIPTOR = msg({
	message: 'Nothing here yet',
	comment: 'Profile decorations settings: empty state title when the user owns no decoration.',
});
const EMPTY_OWNED_BODY_DESCRIPTOR = msg({
	message: 'Decorations you buy or are given show up here.',
	comment: 'Profile decorations settings: empty state text when the user owns no decoration.',
});
const OPEN_SHOP_DESCRIPTOR = msg({
	message: 'Visit the shop',
	comment: 'Profile decorations settings: button that opens the shop.',
});
const PLUS_STRIP_TITLE_DESCRIPTOR = msg({
	message: 'More with {premiumProductName}',
	comment: 'Profile decorations settings: title of the band that invites users without Plus to get it.',
});
const PLUS_STRIP_BODY_DESCRIPTOR = msg({
	message: '{count} decorations in this list, name styles with 12 fonts and a two-color profile theme.',
	comment:
		'Profile decorations settings: what Plus unlocks, shown to users without Plus. {count} is the number of Plus decorations.',
});
const PLUS_STRIP_BUTTON_DESCRIPTOR = msg({
	message: 'See {premiumProductName}',
	comment: 'Profile decorations settings: button that opens the Plus page. Preserve {premiumProductName}.',
});
const UNLOCK_WITH_PLUS_DESCRIPTOR = msg({
	message: 'Unlock with {premiumProductName}',
	comment: 'Profile decorations settings: button under a locked Plus decoration. Preserve {premiumProductName}.',
});
const TAKEN_OFF_DESCRIPTOR = msg({
	message: '{worn} is on, so {removed} came off.',
	comment:
		'Profile decorations settings toast: wearing an ultra decoration takes the avatar decoration off and the other way round. {worn} and {removed} are slot names.',
});

// The catalog also carries the room character and its parts; they have their own form below the profile slots.
function isProfileItem(item: CosmeticItem): boolean {
	return (COSMETIC_TYPES as ReadonlyArray<string>).includes(item.type);
}

function profileItems(): Array<CosmeticItem> {
	return Cosmetics.items.filter(isProfileItem);
}

function toAssetData(item: CosmeticItem | null): CosmeticAssetDataResponse | null {
	return item?.asset ? {asset: item.asset, item_id: item.id} : null;
}

function pendingChanges(draft: CosmeticSelection): CosmeticSelection {
	return Object.fromEntries(
		Object.entries(draft).filter(([type, itemId]) => itemId !== Cosmetics.equippedId(type as CosmeticType)),
	);
}

function openShop(): void {
	ModalCommands.popAll();
	RouterUtils.transitionTo(Routes.SHOP);
}

interface CosmeticTileProps {
	item: CosmeticItem | null;
	type: CosmeticType;
	selected: boolean;
	locked: boolean;
	onSelect: (type: CosmeticType, item: CosmeticItem | null) => void;
}

// Tiles show the still image; a moving one plays only under the pointer, so a full grid decodes nothing in a loop.
const CosmeticTile = observer(function CosmeticTile({item, type, selected, locked, onSelect}: CosmeticTileProps) {
	const {i18n} = useLingui();
	const [hovering, setHovering] = useState(false);
	const playing = useShouldAnimate({kind: 'avatar', isHovering: hovering && !Accessibility.useReducedMotion});
	const name = item ? cosmeticName(item, i18n.locale) : i18n._(NONE_DESCRIPTOR);
	const label = locked ? i18n._(LOCKED_TILE_DESCRIPTOR, {name, premiumProductName: PREMIUM_PRODUCT_NAME}) : name;
	const assetData = toAssetData(item);
	const wide = type === 'nameplate';
	const imageUrl = assetData && !wide ? getCosmeticAssetURL(type, assetData, TILE_IMAGE_CSS_SIZE, playing) : '';
	const user = Users.currentUser;
	return (
		<div className={styles.tileWrapper} data-flx="cosmetics.cosmetics-settings-tab.tile-wrapper">
			<FocusRing offset={-2} data-flx="cosmetics.cosmetics-settings-tab.focus-ring">
				<button
					type="button"
					className={clsx(
						styles.tile,
						wide && styles.tileWide,
						selected && styles.tileSelected,
						locked && styles.tileLocked,
						!item && styles.tileNone,
					)}
					aria-pressed={selected}
					aria-label={label}
					onClick={() => onSelect(type, item)}
					onPointerEnter={() => setHovering(true)}
					onPointerLeave={() => setHovering(false)}
					data-type={type}
					data-flx="cosmetics.cosmetics-settings-tab.tile"
				>
					{item && wide && user ? (
						<NameplateBackground nameplate={assetData} data-flx="cosmetics.cosmetics-settings-tab.tile-nameplate">
							<span className={styles.plateRow} data-flx="cosmetics.cosmetics-settings-tab.tile-plate-row">
								<Avatar user={user} size={24} data-flx="cosmetics.cosmetics-settings-tab.tile-plate-avatar" />
								<span className={styles.plateName} data-flx="cosmetics.cosmetics-settings-tab.tile-plate-name">
									{user.displayName}
								</span>
							</span>
						</NameplateBackground>
					) : item ? (
						imageUrl && (
							<img
								className={clsx(styles.tileImage, type === 'nameplate' && styles.tileImageWide)}
								src={imageUrl}
								alt=""
								loading="lazy"
								draggable={false}
								data-flx="cosmetics.cosmetics-settings-tab.tile-image"
							/>
						)
					) : (
						<ProhibitIcon
							className={styles.noneIcon}
							weight="bold"
							data-flx="cosmetics.cosmetics-settings-tab.none-icon"
						/>
					)}
					{selected && item && (
						<span className={styles.wornBadge} data-flx="cosmetics.cosmetics-settings-tab.worn-badge">
							<CheckIcon weight="bold" data-flx="cosmetics.cosmetics-settings-tab.worn-icon" />
							{i18n._(WORN_BADGE_DESCRIPTOR)}
						</span>
					)}
					{locked && (
						<span className={styles.lockBadge} data-flx="cosmetics.cosmetics-settings-tab.lock-badge">
							<LockSimpleIcon weight="fill" data-flx="cosmetics.cosmetics-settings-tab.lock-icon" />
						</span>
					)}
				</button>
			</FocusRing>
			<span className={styles.tileName} aria-hidden data-flx="cosmetics.cosmetics-settings-tab.tile-name">
				{name}
			</span>
		</div>
	);
});

interface TileGridProps {
	items: ReadonlyArray<CosmeticItem>;
	noneType: CosmeticType;
	isPremium: boolean;
	selectedId: (type: CosmeticType) => string | null;
	onSelect: (type: CosmeticType, item: CosmeticItem | null) => void;
	label: string;
}

function TileGrid({items, noneType, isPremium, selectedId, onSelect, label}: TileGridProps) {
	const {i18n} = useLingui();
	const [shown, setShown] = useState(PAGE_SIZE);
	return (
		<>
			<div
				className={clsx(styles.grid, noneType === 'nameplate' && styles.gridWide)}
				role="group"
				aria-label={label}
				data-flx="cosmetics.cosmetics-settings-tab.grid"
			>
				<CosmeticTile
					item={null}
					type={noneType}
					selected={selectedId(noneType) === null}
					locked={false}
					onSelect={onSelect}
					data-flx="cosmetics.cosmetics-settings-tab.cosmetic-tile"
				/>
				{items.slice(0, shown).map((item) => (
					<CosmeticTile
						key={item.id}
						item={item}
						type={item.type as CosmeticType}
						selected={selectedId(item.type as CosmeticType) === item.id}
						locked={isCosmeticLocked(item, isPremium)}
						onSelect={onSelect}
						data-flx="cosmetics.cosmetics-settings-tab.cosmetic-tile"
					/>
				))}
			</div>
			{items.length > shown && (
				<Button
					variant="secondary"
					small={true}
					className={styles.showMore}
					onClick={() => setShown((count) => count + PAGE_SIZE)}
					data-flx="cosmetics.cosmetics-settings-tab.button.show-more"
				>
					{i18n._(SHOW_MORE_DESCRIPTOR, {count: Math.min(PAGE_SIZE, items.length - shown)})}
				</Button>
			)}
		</>
	);
}

interface SlotCardProps {
	type: CosmeticType;
	item: CosmeticItem | null;
	active: boolean;
	onOpen: (type: CosmeticType) => void;
	onTakeOff: (type: CosmeticType) => void;
}

function SlotCard({type, item, active, onOpen, onTakeOff}: SlotCardProps) {
	const {i18n} = useLingui();
	const assetData = toAssetData(item);
	const imageUrl = assetData ? getCosmeticAssetURL(type, assetData, SLOT_IMAGE_CSS_SIZE, false) : '';
	return (
		<div
			className={clsx(styles.slot, active && styles.slotActive, !item && styles.slotEmpty)}
			data-type={type}
			data-flx="cosmetics.cosmetics-settings-tab.slot"
		>
			<button
				type="button"
				className={styles.slotOpen}
				onClick={() => onOpen(type)}
				aria-pressed={active}
				data-flx="cosmetics.cosmetics-settings-tab.slot-open"
			>
				<span className={styles.slotArt} data-flx="cosmetics.cosmetics-settings-tab.slot-art">
					{imageUrl ? (
						<img
							className={clsx(styles.slotImage, type === 'nameplate' && styles.tileImageWide)}
							src={imageUrl}
							alt=""
							draggable={false}
							data-flx="cosmetics.cosmetics-settings-tab.slot-image"
						/>
					) : (
						<ProhibitIcon className={styles.noneIcon} data-flx="cosmetics.cosmetics-settings-tab.slot-none" />
					)}
				</span>
				<span className={styles.slotText} data-flx="cosmetics.cosmetics-settings-tab.slot-text">
					<span className={styles.slotType} data-flx="cosmetics.cosmetics-settings-tab.slot-type">
						{i18n._(COSMETIC_TYPE_LABELS[type])}
					</span>
					<span className={styles.slotName} data-flx="cosmetics.cosmetics-settings-tab.slot-name">
						{item ? cosmeticName(item, i18n.locale) : i18n._(EMPTY_SLOT_DESCRIPTOR)}
					</span>
				</span>
			</button>
			{item && (
				<button
					type="button"
					className={styles.slotTakeOff}
					onClick={() => onTakeOff(type)}
					aria-label={i18n._(TAKE_OFF_SLOT_DESCRIPTOR, {slot: i18n._(COSMETIC_TYPE_LABELS[type])})}
					data-flx="cosmetics.cosmetics-settings-tab.slot-take-off"
				>
					{i18n._(TAKE_OFF_DESCRIPTOR)}
				</button>
			)}
		</div>
	);
}

function PlusStrip({lockedCount}: {lockedCount: number}) {
	const {i18n} = useLingui();
	return (
		<div className={styles.plusStrip} data-flx="cosmetics.cosmetics-settings-tab.plus-strip">
			<span className={styles.plusIcon} aria-hidden data-flx="cosmetics.cosmetics-settings-tab.plus-icon">
				<CrownIcon weight="fill" data-flx="cosmetics.cosmetics-settings-tab.plus-crown" />
			</span>
			<span className={styles.plusText} data-flx="cosmetics.cosmetics-settings-tab.plus-text">
				<span className={styles.plusTitle} data-flx="cosmetics.cosmetics-settings-tab.plus-title">
					{i18n._(PLUS_STRIP_TITLE_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
				</span>
				<span className={styles.plusBody} data-flx="cosmetics.cosmetics-settings-tab.plus-body">
					{i18n._(PLUS_STRIP_BODY_DESCRIPTOR, {count: lockedCount})}
				</span>
			</span>
			<button
				type="button"
				className={styles.plusButton}
				onClick={() => PremiumModalCommands.open()}
				data-flx="cosmetics.cosmetics-settings-tab.button.plus-strip"
			>
				{i18n._(PLUS_STRIP_BUTTON_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
			</button>
		</div>
	);
}

function EmptyInventory({owned}: {owned: boolean}) {
	const {i18n} = useLingui();
	return (
		<div className={styles.empty} data-flx="cosmetics.cosmetics-settings-tab.empty">
			<StorefrontIcon
				className={styles.emptyIcon}
				weight="duotone"
				data-flx="cosmetics.cosmetics-settings-tab.empty-icon"
			/>
			<span className={styles.emptyTitle} data-flx="cosmetics.cosmetics-settings-tab.empty-title">
				{i18n._(owned ? EMPTY_OWNED_TITLE_DESCRIPTOR : NO_RESULTS_DESCRIPTOR)}
			</span>
			{owned && (
				<span className={styles.emptyBody} data-flx="cosmetics.cosmetics-settings-tab.empty-body">
					{i18n._(EMPTY_OWNED_BODY_DESCRIPTOR)}
				</span>
			)}
			<Button small={true} onClick={openShop} data-flx="cosmetics.cosmetics-settings-tab.button.open-shop">
				{i18n._(OPEN_SHOP_DESCRIPTOR)}
			</Button>
		</div>
	);
}

interface ChipProps {
	label: string;
	count?: number;
	pressed: boolean;
	plus?: boolean;
	onClick: () => void;
}

function Chip({label, count, pressed, plus, onClick}: ChipProps) {
	return (
		<button
			type="button"
			className={clsx(styles.chip, pressed && styles.chipPressed, plus && styles.chipPlus)}
			aria-pressed={pressed}
			onClick={onClick}
			data-flx="cosmetics.cosmetics-settings-tab.chip"
		>
			{plus && <CrownIcon weight="fill" data-flx="cosmetics.cosmetics-settings-tab.chip-crown" />}
			<span data-flx="cosmetics.cosmetics-settings-tab.chip-label">{label}</span>
			{count !== undefined && (
				<span className={styles.chipCount} data-flx="cosmetics.cosmetics-settings-tab.chip-count">
					{count}
				</span>
			)}
		</button>
	);
}

interface ToolbarProps {
	query: InventoryQuery;
	setQuery: (update: Partial<InventoryQuery>) => void;
	isPremium: boolean;
}

function Toolbar({query, setQuery, isPremium}: ToolbarProps) {
	const {i18n} = useLingui();
	const sets = inventorySets(profileItems());
	const filters: Array<[InventoryFilter, MessageDescriptor]> = [
		['all', FILTER_ALL_DESCRIPTOR],
		['owned', FILTER_OWNED_DESCRIPTOR],
	];
	if (!isPremium && shouldShowPremiumFeatures()) filters.push(['locked', FILTER_LOCKED_DESCRIPTOR]);
	return (
		<div className={styles.toolbar} data-flx="cosmetics.cosmetics-settings-tab.toolbar">
			<div className={styles.toolbarRow} data-flx="cosmetics.cosmetics-settings-tab.toolbar-row">
				<label className={styles.search} data-flx="cosmetics.cosmetics-settings-tab.search">
					<MagnifyingGlassIcon className={styles.searchIcon} data-flx="cosmetics.cosmetics-settings-tab.search-icon" />
					<input
						type="search"
						className={styles.searchInput}
						value={query.search}
						placeholder={i18n._(SEARCH_DESCRIPTOR)}
						aria-label={i18n._(SEARCH_DESCRIPTOR)}
						onChange={(event) => setQuery({search: event.target.value})}
						data-flx="cosmetics.cosmetics-settings-tab.search-input"
					/>
				</label>
				<div className={styles.segment} data-flx="cosmetics.cosmetics-settings-tab.filters">
					{filters.map(([filter, label]) => (
						<Chip
							key={filter}
							label={i18n._(label, {premiumProductName: PREMIUM_PRODUCT_NAME})}
							pressed={query.filter === filter}
							plus={filter === 'locked'}
							onClick={() => setQuery({filter})}
							data-flx="cosmetics.cosmetics-settings-tab.filter-chip"
						/>
					))}
				</div>
			</div>
			{sets.length > 0 && (
				<div className={styles.chips} data-flx="cosmetics.cosmetics-settings-tab.sets">
					<Chip
						label={i18n._(ALL_SETS_DESCRIPTOR)}
						pressed={query.set === null}
						onClick={() => setQuery({set: null})}
						data-flx="cosmetics.cosmetics-settings-tab.set-chip"
					/>
					{sets.map((set) => (
						<Chip
							key={set}
							label={shopSetName(set, i18n.locale)}
							pressed={query.set === set}
							onClick={() => setQuery({set: query.set === set ? null : set})}
							data-flx="cosmetics.cosmetics-settings-tab.set-chip"
						/>
					))}
				</div>
			)}
		</div>
	);
}

const CosmeticsSettingsTab = observer(function CosmeticsSettingsTab() {
	const {i18n} = useLingui();
	// Picks are previewed locally and saved together: every save fans the user change out to all their communities.
	const [draft, setDraft] = useState<CosmeticSelection>({});
	// The room character saves through its own endpoints and never fans out, but rides the same unsaved changes bar.
	const [roomEditor, setRoomEditor] = useState<RoomOutfitEditorFormState | null>(null);
	const [lockedItem, setLockedItem] = useState<CosmeticItem | null>(null);
	const [saving, setSaving] = useState(false);
	const [query, setQueryState] = useState<InventoryQuery>(ALL_QUERY);
	const setQuery = useCallback(
		(update: Partial<InventoryQuery>) => setQueryState((previous) => ({...previous, ...update})),
		[],
	);
	const showRoomCharacter = readVoiceRoomAssignment(ExperimentAssignments.response).avatars;
	useEffect(() => {
		void Cosmetics.load();
		return () => UnsavedChangesCommands.clearUnsavedChanges(TAB_ID);
	}, []);
	const hasChanges = Object.keys(pendingChanges(draft)).length > 0 || (roomEditor?.hasChanges ?? false);
	const handleReset = useCallback(() => {
		setDraft({});
		roomEditor?.reset();
	}, [roomEditor]);
	const handleSave = useCallback(async () => {
		const changes = pendingChanges(draft);
		const slotsChanged = Object.keys(changes).length > 0;
		const roomChanged = roomEditor?.hasChanges ?? false;
		if (!slotsChanged && !roomChanged) return;
		setSaving(true);
		try {
			await Promise.all([slotsChanged && Cosmetics.equip(changes), roomChanged && roomEditor?.save()]);
			setDraft({});
			roomEditor?.reset();
		} catch {
			ToastCommands.createToast({type: 'error', children: i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR)});
		} finally {
			setSaving(false);
		}
	}, [draft, roomEditor, i18n]);
	useEffect(() => {
		UnsavedChangesCommands.setUnsavedChanges(TAB_ID, hasChanges);
	}, [hasChanges]);
	useEffect(() => {
		UnsavedChangesCommands.setTabData(TAB_ID, {onReset: handleReset, onSave: handleSave, isSubmitting: saving});
	}, [handleReset, handleSave, saving]);
	const user = Users.currentUser;
	if (Cosmetics.failed) {
		return (
			<StatusSlate
				Icon={WarningIcon}
				title={i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR)}
				description={i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR)}
				actions={[{text: i18n._(TRY_AGAIN_DESCRIPTOR), onClick: () => void Cosmetics.load()}]}
				data-flx="cosmetics.cosmetics-settings-tab.status-slate"
			/>
		);
	}
	if (!Cosmetics.ready || !user) {
		return (
			<div className={styles.spinnerContainer} data-flx="cosmetics.cosmetics-settings-tab.spinner-container">
				<Spinner data-flx="cosmetics.cosmetics-settings-tab.spinner" />
			</div>
		);
	}
	const isPremium = user.isPremium();
	const lockedCount = isPremium ? 0 : profileItems().filter((item) => isCosmeticLocked(item, false)).length;
	const offerPlus = lockedCount > 0 && shouldShowPremiumFeatures();
	const selectedId = (type: CosmeticType) => (type in draft ? (draft[type] ?? null) : Cosmetics.equippedId(type));
	const handleSelect = (type: CosmeticType, item: CosmeticItem | null) => {
		if (item && isCosmeticLocked(item, isPremium)) {
			setLockedItem(item);
			return;
		}
		setLockedItem(null);
		const picked = pickCosmetic(draft, type, item?.id ?? null, Cosmetics.equippedId);
		setDraft(picked.draft);
		if (picked.takenOff) {
			ToastCommands.createToast({
				type: 'info',
				children: i18n._(TAKEN_OFF_DESCRIPTOR, {
					worn: i18n._(COSMETIC_TYPE_LABELS[type]),
					removed: i18n._(COSMETIC_TYPE_LABELS[picked.takenOff]),
				}),
			});
		}
	};
	const handleTakeOff = (type: CosmeticType) => handleSelect(type, null);
	const handleOpenSlot = (type: CosmeticType) => setQuery({type: query.type === type ? 'all' : type});
	// Untouched slots keep the worn data from the user, so an item the catalog dropped still shows.
	const previewUser = user.withUpdates({
		...('avatar_decoration' in draft && {
			avatar_decoration_data: toAssetData(Cosmetics.getItem(selectedId('avatar_decoration'))),
		}),
		...('avatar_ultra' in draft && {avatar_ultra_data: toAssetData(Cosmetics.getItem(selectedId('avatar_ultra')))}),
		...('nameplate' in draft && {nameplate_data: toAssetData(Cosmetics.getItem(selectedId('nameplate')))}),
	});
	const filtered = filterInventory(profileItems(), query, isPremium, i18n.locale);
	const ownedEmpty = query.filter === 'owned' && query.search === '' && query.set === null;
	const gridProps = {isPremium, selectedId, onSelect: handleSelect};

	const slots = (
		<section
			className={styles.slots}
			aria-label={i18n._(WORN_TITLE_DESCRIPTOR)}
			data-flx="cosmetics.cosmetics-settings-tab.slots"
		>
			<h3 className={styles.sectionLabel} data-flx="cosmetics.cosmetics-settings-tab.slots-title">
				{i18n._(WORN_TITLE_DESCRIPTOR)}
			</h3>
			<div className={styles.slotList} data-flx="cosmetics.cosmetics-settings-tab.slot-list">
				{COSMETIC_TYPES.map((type) => (
					<SlotCard
						key={type}
						type={type}
						item={Cosmetics.getItem(selectedId(type))}
						active={query.type === type}
						onOpen={handleOpenSlot}
						onTakeOff={handleTakeOff}
						data-flx="cosmetics.cosmetics-settings-tab.slot-card"
					/>
				))}
			</div>
		</section>
	);

	const preview = (
		<div className={styles.previewColumn} data-flx="cosmetics.cosmetics-settings-tab.preview-column">
			<ProfilePreview
				user={previewUser}
				previewProfileEffect={toAssetData(Cosmetics.getItem(selectedId('profile_effect')))}
				showMembershipInfo={false}
				showMessageButton={false}
				data-flx="cosmetics.cosmetics-settings-tab.profile-preview"
			/>
			<div className={styles.nameplatePreview} data-flx="cosmetics.cosmetics-settings-tab.nameplate-preview">
				<NameplateBackground
					nameplate={previewUser.nameplate}
					data-flx="cosmetics.cosmetics-settings-tab.nameplate-background"
				>
					<div className={styles.nameplateRow} data-flx="cosmetics.cosmetics-settings-tab.nameplate-row">
						<Avatar user={previewUser} size={32} data-flx="cosmetics.cosmetics-settings-tab.avatar" />
						<span className={styles.nameplateName} data-flx="cosmetics.cosmetics-settings-tab.nameplate-name">
							{previewUser.displayName}
						</span>
					</div>
				</NameplateBackground>
			</div>
		</div>
	);

	const types = query.type === 'all' ? COSMETIC_TYPES : [query.type];
	const sections = types
		.map((type) => ({type, items: filtered.filter((item) => item.type === type)}))
		.filter((section) => section.items.length > 0);

	return (
		<SettingsTabContainer data-flx="cosmetics.cosmetics-settings-tab.settings-tab-container">
			<SettingsTabContent data-flx="cosmetics.cosmetics-settings-tab.settings-tab-content">
				<div className={styles.layout} data-flx="cosmetics.cosmetics-settings-tab.layout">
					<aside className={styles.loadout} data-flx="cosmetics.cosmetics-settings-tab.loadout">
						{preview}
						{slots}
						<EditBadgesButton user={user} data-flx="cosmetics.cosmetics-settings-tab.edit-badges-button" />
					</aside>
					<div className={styles.pickers} data-flx="cosmetics.cosmetics-settings-tab.pickers">
						{offerPlus && (
							<PlusStrip lockedCount={lockedCount} data-flx="cosmetics.cosmetics-settings-tab.plus-strip-root" />
						)}
						<Toolbar
							query={query}
							setQuery={setQuery}
							isPremium={isPremium}
							data-flx="cosmetics.cosmetics-settings-tab.toolbar-root"
						/>
						{lockedItem && isProfileItem(lockedItem) && (
							<div className={styles.lockedNotice} data-flx="cosmetics.cosmetics-settings-tab.locked-notice">
								<LockSimpleIcon
									weight="fill"
									className={styles.lockedIcon}
									data-flx="cosmetics.cosmetics-settings-tab.locked-icon"
								/>
								<span className={styles.lockedText} data-flx="cosmetics.cosmetics-settings-tab.locked-text">
									{i18n._(LOCKED_DESCRIPTOR, {
										name: cosmeticName(lockedItem, i18n.locale),
										premiumProductName: PREMIUM_PRODUCT_NAME,
									})}
								</span>
								{shouldShowPremiumFeatures() && (
									<Button
										small={true}
										onClick={() => PremiumModalCommands.open()}
										data-flx="cosmetics.cosmetics-settings-tab.button.get-premium"
									>
										{i18n._(UNLOCK_WITH_PLUS_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
									</Button>
								)}
							</div>
						)}
						{sections.length === 0 ? (
							<EmptyInventory owned={ownedEmpty} data-flx="cosmetics.cosmetics-settings-tab.empty-inventory" />
						) : (
							sections.map(({type, items}) => (
								<section
									key={type}
									className={styles.typeSection}
									data-type={type}
									data-flx="cosmetics.cosmetics-settings-tab.type-section"
								>
									<header className={styles.typeHeader} data-flx="cosmetics.cosmetics-settings-tab.type-header">
										<h3 className={styles.typeTitle} data-flx="cosmetics.cosmetics-settings-tab.type-title">
											{i18n._(COSMETIC_TYPE_LABELS[type])}
										</h3>
										<span
											className={styles.typeDescription}
											data-flx="cosmetics.cosmetics-settings-tab.type-description"
										>
											{i18n._(COSMETIC_TYPE_DESCRIPTIONS[type])}
										</span>
										{query.type !== 'all' && (
											<button
												type="button"
												className={styles.allTypes}
												onClick={() => setQuery({type: 'all'})}
												data-flx="cosmetics.cosmetics-settings-tab.all-types"
											>
												{i18n._(ALL_TYPES_DESCRIPTOR)}
											</button>
										)}
									</header>
									<TileGrid
										key={`${query.filter}-${query.set}-${query.search}`}
										items={items}
										noneType={type}
										label={i18n._(COSMETIC_TYPE_LABELS[type])}
										{...gridProps}
										data-flx="cosmetics.cosmetics-settings-tab.tile-grid"
									/>
								</section>
							))
						)}
					</div>
				</div>
				{showRoomCharacter && (
					<SettingsSection
						id={`profile-cosmetics-${ROOM_CHARACTER_TYPE}`}
						linkable={false}
						title={i18n._(COSMETIC_TYPE_LABELS.room_character)}
						description={i18n._(COSMETIC_TYPE_DESCRIPTIONS.room_character)}
						data-flx="cosmetics.cosmetics-settings-tab.settings-section--room-character"
					>
						<RoomOutfitEditorForm
							onStateChange={setRoomEditor}
							lockState={{item: lockedItem, onChange: setLockedItem}}
							data-flx="cosmetics.cosmetics-settings-tab.room-outfit-editor-form"
						/>
					</SettingsSection>
				)}
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default CosmeticsSettingsTab;
